import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { fileToResizedDataURL } from "@/lib/image";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMOTIONS = ["Calm", "Confident", "Neutral", "FOMO", "Fearful", "Greedy", "Revenge"];
const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));

function fromEntry(e) {
  return {
    date: e?.date || new Date().toISOString().slice(0, 10),
    symbol: e?.symbol || "",
    direction: e?.direction || "long",
    segment: e?.segment || "equity",
    entry_price: e?.entry_price ?? "",
    exit_price: e?.exit_price ?? "",
    quantity: e?.quantity ?? "",
    stop_loss: e?.stop_loss ?? "",
    target: e?.target ?? "",
    setup: e?.setup || "",
    reason_entry: e?.reason_entry || "",
    reason_exit: e?.reason_exit || "",
    mistakes: e?.mistakes || "",
    emotion: e?.emotion || "",
    notes: e?.notes || "",
    tags: (e?.tags || []).join(", "),
    chart_image: e?.chart_image || "",
  };
}

export function JournalEntryDialog({ entry, open, onOpenChange, onSaved }) {
  const [f, setF] = useState(fromEntry(entry));
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(fromEntry(entry)); }, [open]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const livePnl = (() => {
    const e = num(f.entry_price), x = num(f.exit_price), q = num(f.quantity);
    if (e === null || x === null || q === null) return null;
    const diff = f.direction === "long" ? x - e : e - x;
    return Math.round(diff * q * 100) / 100;
  })();

  const onChart = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const url = await fileToResizedDataURL(file, { max: 1100 }); setF((s) => ({ ...s, chart_image: url })); }
    catch { toast.error("Could not read image."); }
  };

  const save = async () => {
    if (!f.symbol.trim()) return toast.error("Please enter a symbol.");
    setSaving(true);
    try {
      const payload = {
        ...f,
        entry_price: num(f.entry_price), exit_price: num(f.exit_price), quantity: num(f.quantity),
        stop_loss: num(f.stop_loss), target: num(f.target),
        tags: f.tags.split(",").map((t) => t.trim().replace(/^#/, "")).filter(Boolean),
      };
      if (entry?.id) await api.put(`/me/journal/${entry.id}`, payload);
      else await api.post("/me/journal", payload);
      toast.success(entry?.id ? "Entry updated" : "Trade logged");
      await onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const Field = ({ label, k, type = "text", placeholder }) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type={type} placeholder={placeholder} value={f[k]} onChange={set(k)} data-testid={`journal-${k}`} />
    </div>
  );
  const Area = ({ label, k, placeholder }) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <textarea rows={2} placeholder={placeholder} value={f[k]} onChange={set(k)} data-testid={`journal-${k}`}
        className="w-full rounded-md border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid="journal-dialog">
        <DialogHeader><DialogTitle>{entry?.id ? "Edit Trade" : "Log a Trade"}</DialogTitle></DialogHeader>

        <div className="grid sm:grid-cols-3 gap-3">
          {Field({ label: "Date", k: "date", type: "date" })}
          {Field({ label: "Symbol", k: "symbol", placeholder: "NIFTY" })}
          <div className="space-y-1">
            <Label className="text-xs">Direction</Label>
            <Select value={f.direction} onValueChange={(v) => setF((s) => ({ ...s, direction: v }))}>
              <SelectTrigger data-testid="journal-direction"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="long">Long</SelectItem><SelectItem value="short">Short</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Segment</Label>
            <Select value={f.segment} onValueChange={(v) => setF((s) => ({ ...s, segment: v }))}>
              <SelectTrigger data-testid="journal-segment"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="equity">Equity</SelectItem><SelectItem value="futures">Futures</SelectItem><SelectItem value="options">Options</SelectItem></SelectContent>
            </Select>
          </div>
          {Field({ label: "Entry price", k: "entry_price", type: "number" })}
          {Field({ label: "Exit price", k: "exit_price", type: "number" })}
          {Field({ label: "Quantity", k: "quantity", type: "number" })}
          {Field({ label: "Stop-loss", k: "stop_loss", type: "number" })}
          {Field({ label: "Target", k: "target", type: "number" })}
        </div>

        {livePnl !== null && (
          <div className={`rounded-md px-3 py-2 text-sm font-semibold ${livePnl > 0 ? "bg-emerald-50 text-emerald-700" : livePnl < 0 ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-600"}`} data-testid="journal-live-pnl">
            Estimated P&L: {livePnl < 0 ? "-" : ""}₹{Math.abs(livePnl).toLocaleString("en-IN")} · {livePnl > 0 ? "Win" : livePnl < 0 ? "Loss" : "Break-even"}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          {Field({ label: "Setup / Strategy", k: "setup", placeholder: "Breakout, CPR, OI shift…" })}
          <div className="space-y-1">
            <Label className="text-xs">Emotion / Confidence</Label>
            <Select value={f.emotion || "none"} onValueChange={(v) => setF((s) => ({ ...s, emotion: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="journal-emotion"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {EMOTIONS.map((em) => <SelectItem key={em} value={em}>{em}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {Area({ label: "Reason for entry", k: "reason_entry", placeholder: "Why did you take this trade?" })}
        {Area({ label: "Reason for exit", k: "reason_exit", placeholder: "Why did you close it?" })}
        {Area({ label: "Mistakes / Lessons", k: "mistakes", placeholder: "What could be improved?" })}
        {Area({ label: "Notes", k: "notes" })}
        {Field({ label: "Tags (comma separated)", k: "tags", placeholder: "breakout, discipline" })}

        <div className="space-y-1">
          <Label className="text-xs">Chart screenshot</Label>
          <input type="file" accept="image/*" onChange={onChart} data-testid="journal-chart-input" className="block text-sm" />
          {f.chart_image && (
            <div className="mt-2 relative inline-block">
              <img src={f.chart_image} alt="chart" className="h-32 rounded-md border border-border object-cover" />
              <button onClick={() => setF((s) => ({ ...s, chart_image: "" }))} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-600 text-white text-xs" data-testid="journal-remove-chart">×</button>
            </div>
          )}
        </div>

        <DialogFooter>
          <button onClick={save} disabled={saving} data-testid="journal-save-btn"
            className="h-10 px-5 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors disabled:opacity-60">
            {saving ? "Saving…" : entry?.id ? "Update entry" : "Save trade"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
